import React from 'react';

import { css } from '@emotion/css';
import { Form } from 'antd';
import { FormItemProps } from 'antd/lib/form/FormItem';

import { Select } from 'components/Select';

const MONTHS = [
  { value: '1', label: 'January' },
  { value: '2', label: 'February' },
  { value: '3', label: 'March' },
  { value: '4', label: 'April' },
  { value: '5', label: 'May' },
  { value: '6', label: 'June' },
  { value: '7', label: 'July' },
  { value: '8', label: 'August' },
  { value: '9', label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' },
];

// Use year 2000 (a leap year) to support Feb 29
function getDaysInMonth(month: number): number {
  return new Date(2000, month, 0).getDate();
}

function parseMonthDay(value: string | null | undefined): { month: string | undefined; day: string | undefined } {
  if (!value) return { month: undefined, day: undefined };
  const parts = value.split('/');
  if (parts.length !== 2) return { month: undefined, day: undefined };
  const month = parseInt(parts[0], 10);
  const day = parseInt(parts[1], 10);
  if (isNaN(month) || isNaN(day)) return { month: undefined, day: undefined };
  return { month: String(month), day: String(day) };
}

function formatMonthDay(month: string, day: string): string {
  return `${month.padStart(2, '0')}/${day.padStart(2, '0')}`;
}

interface MonthDayPickerProps {
  value?: string | null;
  onChange?: (value: string | null) => void;
  disabled?: boolean;
  allowClear?: boolean;
}

const MonthDayPicker = ({ value, onChange, disabled, allowClear }: MonthDayPickerProps) => {
  const { month, day } = parseMonthDay(value);
  const daysInSelectedMonth = month ? getDaysInMonth(parseInt(month, 10)) : 31;
  const dayOptions = Array.from({ length: daysInSelectedMonth }, (_, i) => ({
    value: String(i + 1),
    label: String(i + 1),
  }));

  const handleMonthChange = (newMonth: string | undefined) => {
    if (newMonth === undefined) {
      onChange?.(null);
      return;
    }
    const maxDays = getDaysInMonth(parseInt(newMonth, 10));
    const currentDay = day !== undefined ? parseInt(day, 10) : 1;
    const newDay = String(Math.min(currentDay, maxDays));
    onChange?.(formatMonthDay(newMonth, newDay));
  };

  const handleDayChange = (newDay: string | undefined) => {
    if (newDay === undefined) {
      onChange?.(null);
      return;
    }
    if (month !== undefined) {
      onChange?.(formatMonthDay(month, newDay));
    }
  };

  return (
    <div
      className={css`
        display: flex;
        gap: 8px;
      `}
    >
      <Select
        placeholder="Month"
        value={month}
        onChange={handleMonthChange}
        options={MONTHS}
        disabled={disabled}
        allowClear={allowClear}
        className={css`
          flex: 2;
        `}
      />
      <Select
        placeholder="Day"
        value={day}
        onChange={handleDayChange}
        options={dayOptions}
        disabled={disabled || month === undefined}
        className={css`
          flex: 1;
        `}
      />
    </div>
  );
};

export interface MonthDayFormItemProps extends Omit<FormItemProps, 'name'> {
  name: string;
  disabled?: boolean;
  allowClear?: boolean;
}

export const MonthDayFormItem = ({
  label,
  name,
  rules = [],
  disabled,
  allowClear,
  ...otherProps
}: MonthDayFormItemProps) => (
  <Form.Item
    label={label}
    name={name}
    rules={[
      ...rules,
      {
        validator: (_rule, value: string | null) => {
          if (!value) return Promise.resolve();
          const { month, day } = parseMonthDay(value);
          if (!month || !day) return Promise.reject(new Error('Invalid date format'));
          const monthNum = parseInt(month, 10);
          const dayNum = parseInt(day, 10);
          if (monthNum < 1 || monthNum > 12) return Promise.reject(new Error('Month must be between 1 and 12'));
          const maxDays = getDaysInMonth(monthNum);
          if (dayNum < 1 || dayNum > maxDays)
            return Promise.reject(new Error(`Day must be between 1 and ${maxDays} for the selected month`));
          return Promise.resolve();
        },
      },
    ]}
    {...otherProps}
  >
    <MonthDayPicker disabled={disabled} allowClear={allowClear} />
  </Form.Item>
);
